import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, AlertCircle, Lock } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SetPassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { updateUserData, logout } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/set-password`, {
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      updateUserData({ password_initialized: true });
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0776BD] via-[#055A92] to-[#044A7A] flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img
              src="/mr-electric-logo.jpg"
              alt="Mr. Electric"
              className="h-24 w-auto object-contain rounded-lg bg-white p-2 shadow-lg"
            />
          </div>
          <p className="text-white/70 mt-1">Inventory Control</p>
        </div>

        <Card className="border-0 shadow-xl rounded-xl">
          <CardHeader className="space-y-1 pb-4">
            <div className="flex items-center gap-2">
              <Lock size={22} className="text-[#0776BD]" />
              <CardTitle className="font-heading text-2xl tracking-tight text-[#1F2937]">Set Your Password</CardTitle>
            </div>
            <CardDescription className="text-[#6B7280]">
              You're logging in for the first time. Please set a secure password to continue.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4 rounded-lg">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-sm font-medium text-[#1F2937]">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  placeholder="Min. 6 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  data-testid="set-password-input"
                  className="h-12 rounded-lg border-gray-300 focus:border-[#0776BD]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-[#1F2937]">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="set-password-confirm-input"
                  className="h-12 rounded-lg border-gray-300 focus:border-[#0776BD]"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-[#0776BD] hover:bg-[#055A92] text-white font-semibold rounded-lg shadow-sm"
                disabled={loading}
                data-testid="set-password-submit-btn"
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Setting Password...</>
                ) : (
                  'Set Password & Continue'
                )}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <button
                onClick={logout}
                className="text-sm text-[#6B7280] hover:text-[#0776BD] hover:underline"
                data-testid="set-password-logout-link"
              >
                Sign out instead
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SetPassword;
